import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Image from "next/legacy/image";
import Script from "next/script";

export default function AmericanLandOwnershipArticle({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={
              "/static/images/articles/history-american-landownership-sharecropping--sm.1x.min.webp"
            }
            alt={"American land"}
            width={900}
            height={565}
          />
        </div>
        <h1 className={styles["Article__heading"]}>
          History of American Landownership and Sharecropping
        </h1>
        <ArticleAuthor />
        <p>
          Landownership has been tied into the American identity since the very
          beginning of the country's history. Today in the United States, almost
          all citizens over the age of 18 are eligible to vote, but in the past,
          you had to own land, too. When the country was founded, voting was
          only open to white men over the age of 21 who owned land. Owning land
          is no longer a requirement to vote, but homeownership is still a
          cornerstone of the American Dream.
        </p>
        <h2 className={styles["Article__sub-heading"]}>What Is a Landowner?</h2>
        <p>
          The simplest definition of a landowner is someone who owns a piece of
          land. It doesn't have to be a person: Businesses, governments,
          churches, nonprofits, and other entities can all be the legal owner of
          a piece of land. There are also many different types of ownership.
          Common forms of ownership are individual ownership, where one person
          is the legal owner and the land transfers to that person's heirs upon
          their death; joint tenants, when two or more people own the same piece
          of land and when one of the dies, ownership of their share of the
          property transfers to the other owner(s); and tenants in common, where
          two or more people own the land but each owner can will their
          ownership share to their heirs upon their death. Businesses often hold
          land in a limited liability corporation or general partnership.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://revenuedata.doi.gov/how-revenue-works/ownership/"
            >
              Landownership
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://masswoods.org/estate-planning-professionals/forms-land-ownership"
            >
              Forms of Landownership
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.hoover.org/research/consequences-land-ownership"
            >
              The Consequences of Landownership
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.farmers.gov/working-with-us/heirs-property-eligibility"
            >
              Heirs' Property Landowners
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.extension.iastate.edu/agdm/wholefarm/html/c2-05.html"
            >
              Leasing and Landownership Terms
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.economics.hawaii.edu/research/workingpapers/WP_02-13.pdf"
            >
              Land Tenure: An Introduction
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://forestry.ca.uky.edu/sites/forestry.ca.uky.edu/files/forfs99-04.pdf"
            >
              Property Rights and Landowner Responsibilities
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          History and Evolution of Landownership
        </h2>
        <p>
          In their earliest histories, nearly all societies around the world
          considered land as property of the community as a whole; no one person
          owned land. During the time of hunters and gathers, this made sense.
          The development of farming led to the idea of owned property. In
          Europe, many of the rights and responsibilities accorded to landowners
          evolved from Roman laws, and these ideas were later brought to
          America. One thing that encouraged settlement of North America is that
          Europe is a relatively small continent and there's only so much land.
          By the 1600s, it was hard for average people to acquire land, but
          there was plenty of land in North America. This desire for
          landownership is why many people chose to immigrate to America, where
          colonial governments often gave away tracts of acreage known as land
          grants to immigrants who qualified. Later in the 19th century, when
          America was an established country, the United States government used
          this same idea to populate the west.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.context.org/iclib/ic08/gilman1/"
            >
              The Idea of Owning Land
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.youtube.com/watch?v=apVIH2D4_Ks"
            >
              History of Landownership
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.leverforchange.org/challenges/explore-challenges/racial-equity-2030/indigenous-lands-initiative-securing-land-ownership-rights-for-indigenous-communities/"
            >
              Indigenous Lands Initiative: Securing Landownership Rights for
              Indigenous Communities
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.investopedia.com/articles/07/history-real-estate.asp"
            >
              No Longer Nomads: The History of{" "}
            </a>
            <a href="https://www.realestateagents.com/nc/raleigh">
              Real Estate
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.kcrw.com/culture/shows/life-examined/land-ownership-indigenous-sacred-places"
            >
              The Role of Land and Landownership in Shaping Our History
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.henrygeorge.org/pchp29.htm">
              History of Land as Private Property
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.hillsdale.edu/wp-content/uploads/2016/02/FMF-2008-Property-Rights-in-American-History.pdf"
            >
              Property Rights in American History
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          What Is Sharecropping?
        </h2>
        <p>
          Sharecropping is a system of farming where people rent farmland from a
          landowner. Instead of paying the rent in money, they pay with a
          portion of the crops they grow on the land. It's a very old system
          that has been used around the world. However, when Americans hear the
          term "sharecropping," they usually think of the way it was practiced
          after the Civil War. People who had owned enslaved people now needed
          laborers to work their land, so they turned to the formerly enslaved
          people to work the land as sharecroppers. This era of sharecropping is
          now seen as having been abusive and economically disadvantageous to
          the sharecroppers.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.pbs.org/tpt/slavery-by-another-name/themes/sharecropping/"
            >
              Slavery by Another Name: Sharecropping
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.battlefields.org/learn/articles/sharecroppers"
            >
              Sharecroppers
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://aaregistry.org/story/sharecropping-a-brief-article/"
            >
              Sharecropping in America
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.okhistory.org/publications/enc/entry.php?entry=TE009"
            >
              Tenant Farming and Sharecropping
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://shec.ashp.cuny.edu/items/show/1885">
              Description of Sharecropping
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          History of Sharecropping
        </h2>
        <p>
          There's some evidence that sharecropping existed in the United States
          before the Civil War in the Mississippi River Valley, specifically
          around Natchez, Mississippi. Some months after the end of the Civil
          War, President Andrew Jackson ordered that all land seized in the
          Confederate states should be returned to the original landowners.
          However, many Southern landowners had depended on enslaved people to
          work their land. Now many of these landowners were cash-poor and had
          no one to plant and harvest crops. At the same time, formerly enslaved
          people had no land or money of their own and needed work. Soon,
          plantations were divided into small lots where one family could work.
          At this point, almost all sharecroppers were black. Soon, though,
          white people also became sharecroppers under this system.
        </p>
        <p>
          The landowners decided what should be planted and how much of the crop
          they would take for rent. The sharecropping system continued for
          decades. By the 1930s, there were 5.5 million white sharecroppers and
          more than 3 million black sharecroppers in the United States.
          Sharecroppers farmed about one-third of all farm acres in Tennessee
          during this period. The Southern Tenant Farmers Union was formed in
          the 1930s as a way to protest against the poor treatment of
          sharecroppers and help them better their lives. Landowners were
          angered by this union, and some landlords condoned violence against
          striking sharecroppers. However, the Great Depression forced more
          people into sharecropping. What eventually ended the practice was
          improvements in technology that allowed machines to do farm work that
          once was done by humans. Many former sharecroppers migrated to cities
          and took jobs in factories instead.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.history.com/topics/black-history/sharecropping#section_1"
            >
              Forty Acres and a Mule
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://texancultures.utsa.edu/cabin/history/#:~:text=The%20history%20of%20sharecropping%20is,due%20to%20the%20Civil%20War."
            >
              History of Sharecropping
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://wams.nyhistory.org/confidence-and-crises/great-depression/sharecroppers/"
            >
              Sharecropping in a Depression
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.mshistorynow.mdah.ms.gov/issue/farmers-without-land-the-plight-of-white-tenant-farmers-and-sharecroppers"
            >
              Farmers Without{" "}
            </a>
            <a href="https://www.realestateagents.com/nc/charlotte">Land</a>
            <a
              rel="nofollow"
              href="https://www.mshistorynow.mdah.ms.gov/issue/farmers-without-land-the-plight-of-white-tenant-farmers-and-sharecroppers"
            >
              : The Plight of White Tenant Farmers and Sharecroppers
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://wfpc.sanford.duke.edu/north-carolina/durham-food-history/sharecropping-black-land-acquisition-and-white-supremacy-1868-1900/"
            >
              Sharecropping, Black Land Acquisition, and White Supremacy,
              1868-1900
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Sharecropping Today</h2>
        <p>
          The newest form of sharecropping started in Britain and is known as
          land-sharing. It works on the idea that a lot of landowners don't
          enjoy gardening or working on their property and a lot of
          apartment-dwellers love working with the soil but don't have the space
          for it. Most cities and some suburbs now have community gardens.
          However, some of those community gardens have long wait lists.
          Land-sharing programs connect landowners with those who wish to have a
          garden. These programs are popular in urban areas as a way to grow
          fresh food. At the start of the season, the landowner and gardener
          come to an agreement, and the gardener usually pays the landowner in
          fresh produce or flowers for using their property.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.dwell.com/article/modern-sharecropping-f5213a08"
            >
              Modern Sharecropping
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://link.springer.com/article/10.1057/s41294-019-00105-z"
            >
              Why Does Sharecropping Survive?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.businessinsider.com/two-forms-of-slavery-that-still-exist-in-america-2012-2"
            >
              Two Forms of Slavery That Still Exist in America
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Fun Facts</h2>
        <p>
          Did you know that Ted Turner, who founded the cable news network CNN,
          owns more than 2 million acres of land? His land holdings are more
          than twice the size of the entire state of Rhode Island! But he's not
          the person who owns the most land in the United States: That title
          goes to John Malone, who made his fortune in telecommunications.
          Malone owns 2.2 million acres of land, including numerous cattle
          ranches. But he doesn't own the most farmland in the country. That
          title actually goes to Bill Gates, founder of Microsoft, who owns
          242,000 acres of farmland. That's definitely a lot, but it's only a
          small portion of the 900 million acres of farmland in the United
          States!
        </p>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "History of American Landownership and Sharecropping | RealEstateAgents.com"
        }
        description={
          "A brief history of landownership and sharecropping in the United States."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/history-american-landownership-sharecropping",
          },
          headline: "History of American Landownership and Sharecropping",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
